import React, { useEffect, useRef, useState } from 'react';
import { X, Gift, CheckCircle } from 'lucide-react';
import { API_URL } from '../config';
import { trackEvent } from '../utils/analytics';
import { formatPrice } from '../utils/formatPrice';

/**
 * Popup „cadou la ieșire” (03.11.2026, adaptat după constatatoare.ro)
 * - doar desktop (mouse, ≥1024px), la ieșirea mouse-ului spre taburi, activ la 2 s după decizia la cookie-uri
 * - o dată la 30 de zile per vizitator (localStorage giftPopupSeen - setat și după plată)
 * - NU cât timp fereastra de cookie-uri (sau altă fereastră) e deschisă / înainte de decizia la cookie-uri
 * - NU pe admin / plată / review, NU cu ?cod= sau ?reluare=, NU dacă formularul are deja o reducere
 * - pornit/oprit din Admin → Setări (giftPopup); codul și sumele vin din /api/orders/features
 */
const SEEN_KEY = 'giftPopupSeen';
const SEEN_DAYS = 30;
const ARM_AFTER_MS = 2000;
const CONSENT_TEXT = 'Sunt de acord să primesc prin email informații despre reduceri, beneficii și noutăți de la CF-Extras.ro. Îmi pot retrage acordul oricând.'; // identic cu backend/services/giftPopupService.js
// Aceleași reguli și mesaje ca în formularul de comandă (CertificateWizard.validateEmailLive)
const EMAIL_RE = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
const emailProblem = (email) => {
  if (!email) return 'Introduceți adresa de email.';
  if (email.includes('..')) return 'Email invalid: puncte duble consecutive';
  if (email.includes(' ')) return 'Email invalid: nu poate conține spații';
  if (!EMAIL_RE.test(email)) return 'Email invalid: format incorect';
  return '';
};

function seenRecently() {
  try {
    const t = Number(localStorage.getItem(SEEN_KEY) || 0);
    return t > 0 && Date.now() - t < SEEN_DAYS * 24 * 3600 * 1000;
  } catch (e) {
    return true; // fără localStorage → nu deranjăm
  }
}

function markSeen() {
  try { localStorage.setItem(SEEN_KEY, String(Date.now())); } catch (e) { /* nimic */ }
}

function cookieDecided() {
  try { return !!localStorage.getItem('cookieConsent'); } catch (e) { return false; }
}

// Fereastra de cookie-uri (z-[9998]/z-[9999]) sau orice altă fereastră pe tot ecranul (ex. setări cookie z-50)
function overlayOpen() {
  return !!document.querySelector('.z-\\[9998\\], .z-\\[9999\\], .fixed.inset-0');
}

// Animații discrete: intrare popup + trecerea formular → cod; oprite la „mișcare redusă”
const ANIM_CSS = `
@keyframes gpIn { from { opacity: 0; transform: translateY(12px) scale(.97); } to { opacity: 1; transform: none; } }
@keyframes gpRise { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: none; } }
@keyframes gpPop { 0% { opacity: 0; transform: scale(.9); } 60% { opacity: 1; transform: scale(1.02); } 100% { transform: scale(1); } }
@keyframes gpCode { from { opacity: 0; letter-spacing: .45em; filter: blur(6px); } to { opacity: 1; letter-spacing: .08em; filter: blur(0); } }
@keyframes gpFade { from { opacity: 0; } to { opacity: 1; } }
@keyframes gpRing { 0%, 30%, 100% { transform: rotate(0); } 4% { transform: rotate(16deg); } 8% { transform: rotate(-14deg); } 12% { transform: rotate(11deg); } 16% { transform: rotate(-8deg); } 20% { transform: rotate(5deg); } 24% { transform: rotate(-2deg); } }
@media (prefers-reduced-motion: reduce) { .gp-anim, .gp-anim * { animation: none !important; transition: none !important; } }
`;
const anim = (name, ms, delay = 0) => ({ animation: `${name} ${ms}ms cubic-bezier(.22,1,.36,1) ${delay}ms both` });

// Card de răzuit: stratul argintiu se șterge cu mouse-ul; la ~45% dispare singur. Click = dezvăluire imediată.
// Fără canvas / „mișcare redusă” → reducerea se vede direct.
const ScratchCard = ({ className, children, onReveal }) => {
  const wrap = useRef(null);
  const canvas = useRef(null);
  const [revealed, setRevealed] = useState(false);
  const [gone, setGone] = useState(false);
  const last = useRef(null);
  const moves = useRef(0);
  const done = useRef(false);

  const reveal = React.useCallback(() => {
    if (done.current) return;
    done.current = true;
    if (onReveal) onReveal();
    setRevealed(true);
    setTimeout(() => setGone(true), 450);
  }, [onReveal]);

  useEffect(() => {
    const c = canvas.current; const box = wrap.current;
    const reduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const ctx = c && c.getContext && c.getContext('2d');
    if (!c || !box || !ctx || reduced) { done.current = true; setRevealed(true); setGone(true); return; }
    const dpr = window.devicePixelRatio || 1;
    const w = box.offsetWidth; const h = box.offsetHeight;
    c.width = w * dpr; c.height = h * dpr; ctx.scale(dpr, dpr);
    const g = ctx.createLinearGradient(0, 0, w, h);
    g.addColorStop(0, '#cbd5e1'); g.addColorStop(0.45, '#f1f5f9'); g.addColorStop(0.55, '#e2e8f0'); g.addColorStop(1, '#94a3b8');
    ctx.fillStyle = g; ctx.fillRect(0, 0, w, h);
    ctx.fillStyle = 'rgba(255,255,255,.55)';
    for (let i = 0; i < 140; i++) { ctx.fillRect(Math.random() * w, Math.random() * h, 1.5, 1.5); }
    ctx.fillStyle = '#334155'; ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
    ctx.font = 'bold 22px system-ui, -apple-system, Segoe UI, sans-serif';
    ctx.fillText('Răzuiți aici', w / 2, h / 2 - 12);
    ctx.font = '14px system-ui, -apple-system, Segoe UI, sans-serif'; ctx.fillStyle = '#475569';
    ctx.fillText('treceți cu mouse-ul peste card', w / 2, h / 2 + 16);
  }, []);

  const scratch = (e) => {
    if (revealed) return;
    const c = canvas.current; if (!c) return;
    const r = c.getBoundingClientRect();
    const x = e.clientX - r.left; const y = e.clientY - r.top;
    const ctx = c.getContext('2d');
    ctx.globalCompositeOperation = 'destination-out';
    ctx.lineCap = 'round'; ctx.lineJoin = 'round'; ctx.lineWidth = 42;
    ctx.beginPath();
    const p = last.current || { x, y };
    ctx.moveTo(p.x, p.y); ctx.lineTo(x, y); ctx.stroke();
    last.current = { x, y };
    moves.current += 1;
    if (moves.current % 8 === 0) {
      const d = ctx.getImageData(0, 0, c.width, c.height).data;
      let clear = 0; let total = 0;
      for (let i = 3; i < d.length; i += 64) { total += 1; if (d[i] === 0) clear += 1; }
      if (clear / total > 0.45) reveal();
    }
  };

  return (
    <div ref={wrap} className={`relative overflow-hidden ${className}`}>
      {children}
      {!gone && (
        <canvas
          ref={canvas}
          onPointerMove={scratch}
          onPointerLeave={() => { last.current = null; }}
          onClick={reveal}
          className={`absolute inset-0 h-full w-full cursor-pointer transition-opacity duration-500 ${revealed ? 'opacity-0' : 'opacity-100'}`}
          style={{ touchAction: 'none' }}
          aria-label="Card de răzuit: click pentru a vedea reducerea"
          role="button"
        />
      )}
    </div>
  );
};

const GiftPopup = () => {
  const [offer, setOffer] = useState(null); // { code, amountCF, amountPC }
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [consent, setConsent] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [emailError, setEmailError] = useState('');
  const [website, setWebsite] = useState(''); // câmp-capcană pentru roboți (invizibil)
  const [result, setResult] = useState(null);
  const [leaving, setLeaving] = useState(false);
  const armed = useRef(false);

  useEffect(() => {
    if (typeof window === 'undefined' || navigator.userAgent === 'ReactSnap') return undefined;
    if (/^\/(admin|payment|review)(\/|$)/.test(window.location.pathname)) return undefined;
    const qs = new URLSearchParams(window.location.search);
    if (qs.get('cod') || qs.get('reluare') || window.__cfResumeToken) return undefined;
    if (!window.matchMedia || !window.matchMedia('(pointer: fine) and (min-width: 1024px)').matches) return undefined;
    if (seenRecently()) return undefined;

    let cancelled = false;
    let timer = null;
    let poll = null;
    fetch(`${API_URL}/api/orders/features`)
      .then((r) => r.json())
      .then((f) => {
        if (cancelled || !f || !f.giftPopup || !f.giftPopup.enabled || !f.giftPopup.code) return;
        setOffer(f.giftPopup);
        const arm = () => { timer = setTimeout(() => { armed.current = true; }, ARM_AFTER_MS); };
        if (cookieDecided()) arm();
        else poll = setInterval(() => { if (cookieDecided()) { clearInterval(poll); poll = null; arm(); } }, 500);
      })
      .catch(() => {});

    const onLeave = (e) => {
      if (!armed.current || e.relatedTarget || e.clientY > 0) return;
      if (/^\/(admin|payment|review)(\/|$)/.test(window.location.pathname)) return; // navigare SPA după încărcare
      if (!cookieDecided() || overlayOpen() || window.__cfHasDiscount || seenRecently()) return;
      armed.current = false;
      markSeen();
      setOpen(true);
      trackEvent('gift_popup_shown');
    };
    document.addEventListener('mouseout', onLeave);
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
      if (poll) clearInterval(poll);
      document.removeEventListener('mouseout', onLeave);
    };
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open]);

  if (!open || !offer) return null;

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    const problem = emailProblem(email.trim());
    if (problem) { setEmailError(problem); return; }
    if (!consent) { setError('Bifați acordul pentru a primi codul pe email.'); return; }
    setSending(true);
    try {
      const res = await fetch(`${API_URL}/api/orders/cadou`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim(), consent: true, website })
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data.success) {
        const next = {
          code: data.code,
          amountCF: data.amountCF,
          amountPC: data.amountPC,
          emailed: data.emailed === true,
          alreadySentAt: data.alreadySentAt || null
        };
        trackEvent('gift_popup_submit');
        setLeaving(true);
        setTimeout(() => { setResult(next); setLeaving(false); }, 220);
      } else {
        setError(data.error || 'Nu am putut trimite codul. Încercați din nou.');
      }
    } catch (err) {
      setError('Nu am putut trimite codul. Încercați din nou.');
    } finally {
      setSending(false);
    }
  };

  // Pe homepage (formularul e acolo) → aplicare fără reload; pe alte pagini → homepage cu ?cod=
  const applyNow = () => {
    const code = result.code;
    setOpen(false);
    if (window.location.pathname === '/' && window.__cfWizardReady) {
      window.dispatchEvent(new CustomEvent('applyGiftCode', { detail: { code } }));
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      window.location.href = `/?cod=${encodeURIComponent(code)}`;
    }
  };

  const amounts = result || offer;

  return (
    <div className="fixed inset-0 z-[9990] flex items-center justify-center bg-black/60 p-4" onClick={() => setOpen(false)} role="presentation">
      <style>{ANIM_CSS}</style>
      <div
        className="gp-anim relative w-full max-w-md rounded-3xl bg-white p-7 shadow-2xl"
        style={anim('gpIn', 320)}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="gift-popup-title"
      >
        <button type="button" onClick={() => setOpen(false)} className="absolute right-4 top-4 rounded-lg p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600" aria-label="Închide">
          <X className="h-5 w-5" />
        </button>

        <div className="flex items-center gap-3">
          <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-emerald-50">
            <Gift className="h-5 w-5 text-emerald-700" style={{ transformOrigin: '50% 15%', animation: 'gpRing 2.6s ease-in-out .5s infinite' }} />
          </span>
          <span className="text-xs font-bold uppercase tracking-wider text-emerald-700">Beneficiu exclusiv</span>
        </div>

        {!result ? (
          <div className={`transition-all duration-200 ease-out ${leaving ? '-translate-y-2 opacity-0' : 'opacity-100'}`}>
            <h2 id="gift-popup-title" className="mt-4 text-2xl font-bold leading-snug text-gray-900">Înainte să plecați, avem o surpriză pentru dumneavoastră.</h2>
            <p className="mt-2 text-gray-600">Beneficiați de o reducere la următoarele comenzi de documente prin CF-Extras.ro.</p>

            <ScratchCard className="mt-5 rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-4 text-center" onReveal={() => trackEvent('gift_popup_scratched')}>
              <p className="text-4xl font-extrabold text-emerald-800">−{formatPrice(amounts.amountCF)} lei</p>
              <p className="mt-1 text-sm text-gray-700">la următoarele comenzi de Extras de Carte Funciară ANCPI</p>
              {amounts.amountPC > 0 && (
                <>
                  <div className="my-3 border-t border-emerald-200" />
                  <p className="font-bold text-emerald-800">−{formatPrice(amounts.amountPC)} lei la Extras de Plan Cadastral ANCPI</p>
                </>
              )}
            </ScratchCard>

            <form onSubmit={submit} className="mt-5" noValidate>
              <p className="text-lg font-bold text-gray-900">Unde vă trimitem codul?</p>
              <label htmlFor="gift-email" className="sr-only">Adresa de email</label>
              <input
                id="gift-email"
                type="email"
                autoComplete="email"
                value={email}
                onChange={(e) => { const v = e.target.value; setEmail(v); setError(''); setEmailError(v ? emailProblem(v) : ''); }}
                placeholder="Adresa de email"
                className="mt-4 w-full rounded-full border border-gray-300 px-5 py-3 text-base focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-500"
              />
              {emailError ? (
                <p className="mt-2 pl-5 text-xs text-red-600">{emailError}</p>
              ) : email && (
                <p className="mt-2 flex items-center gap-1 pl-5 text-xs font-medium text-green-600"><CheckCircle className="h-3.5 w-3.5" />Email valid</p>
              )}
              <input type="text" name="website" value={website} onChange={(e) => setWebsite(e.target.value)} tabIndex={-1} autoComplete="off" aria-hidden="true" className="absolute -left-[9999px] h-0 w-0 opacity-0" />
              <label className="mt-5 flex items-start gap-3 text-xs leading-relaxed text-gray-600">
                <input type="checkbox" checked={consent} onChange={(e) => { setConsent(e.target.checked); setError(''); }} className="mt-0.5 h-5 w-5 flex-shrink-0 rounded-full border-gray-300 text-emerald-600 focus:ring-emerald-500" />
                <span>{CONSENT_TEXT} <a href="/politica-confidentialitate/" target="_blank" rel="noopener noreferrer" className="text-emerald-700 underline">Detalii</a></span>
              </label>
              {error && <p className="mt-2 text-sm text-red-600">{error}</p>}
              <button type="submit" disabled={sending} className="mt-4 w-full rounded-xl bg-emerald-700 py-3.5 text-base font-bold text-white hover:bg-emerald-800 disabled:opacity-60">
                {sending ? 'Se trimite...' : 'Trimite-mi codul de reducere'}
              </button>
              <p className="mt-3 text-center text-xs text-gray-500">Codul va fi trimis prin email. Nu implică nicio obligație de cumpărare.</p>
            </form>
          </div>
        ) : (
          <div aria-live="polite">
            <div style={anim('gpRise', 420)}>
              {result.alreadySentAt ? (
                <>
                  <h2 id="gift-popup-title" className="mt-4 text-2xl font-bold leading-snug text-gray-900">Ne bucurăm să vă revedem!</h2>
                  <p className="mt-2 text-gray-600">
                    Codul vi l-am trimis deja pe email pe {new Date(result.alreadySentAt).toLocaleDateString('ro-RO', { day: 'numeric', month: 'long' })}.
                    Îl regăsiți în inbox, dar vi-l afișăm și aici:
                  </p>
                </>
              ) : (
                <h2 id="gift-popup-title" className="mt-4 text-2xl font-bold leading-snug text-gray-900">Codul dumneavoastră de reducere</h2>
              )}
            </div>
            <div className="mt-4 rounded-2xl border-2 border-dashed border-emerald-500 bg-emerald-50 px-4 py-5 text-center" style={anim('gpPop', 520, 120)}>
              <p className="font-mono text-4xl font-bold text-emerald-800" style={anim('gpCode', 700, 260)}>{result.code}</p>
              <p className="mt-2 text-sm text-gray-700" style={anim('gpFade', 400, 600)}>
                <strong>−{formatPrice(result.amountCF)} lei</strong> la Extras de Carte Funciară ANCPI
                {result.amountPC > 0 && <><br /><strong>−{formatPrice(result.amountPC)} lei</strong> la Extras de Plan Cadastral ANCPI</>}
              </p>
            </div>
            <div style={anim('gpRise', 420, 750)}>
              {result.alreadySentAt ? null : result.emailed ? (
                <p className="mt-3 flex items-center justify-center gap-1.5 text-sm text-gray-600">
                  <CheckCircle className="h-4 w-4 text-emerald-600" /> V-am trimis codul și pe email.
                </p>
              ) : (
                <p className="mt-3 text-center text-sm text-gray-600">Notați codul sau aplicați-l acum.</p>
              )}
              <button type="button" onClick={applyNow} className="mt-5 w-full rounded-xl bg-emerald-700 py-3.5 text-base font-bold text-white hover:bg-emerald-800">
                Aplică reducerea acum
              </button>
              <p className="mt-4 text-center text-sm italic text-gray-600">P.S. Îl puteți da și unui prieten, nu ne supărăm! 😉</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default GiftPopup;
